import { useEffect, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Loader2,
  Lock,
  Shield,
  ShieldAlert,
} from "lucide-react";
import { api, ApiError, type AdminLogEntry, type AdminUser } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ModalShell } from "./AdminModals";
import { Button } from "../ui/Button";
import { StatusBadge } from "../ui/Badges";

type Uni = { id: string; name: string; short_name: string };
type StreamItem = { id: string; name: string; code?: string };

type Props = {
  open: boolean;
  user: AdminUser | null;
  token: string;
  onClose: () => void;
  onSaved: () => void;
  initialTab?: "profile" | "logs";
  inline?: boolean;
};

type UserLogsData = {
  userId: string;
  userName: string;
  userPhone: string;
  plan: string;
  summary: {
    totalEvents: number;
    accessCount: number;
    errorCount: number;
    crucialCount: number;
  };
  recentAccesses: AdminLogEntry[];
  errorsFaced: AdminLogEntry[];
  crucialEvents: AdminLogEntry[];
  items: AdminLogEntry[];
};

export function StudentEditModal({ open, user, token, onClose, onSaved, initialTab = "profile", inline = false }: Props) {
  const { tokens } = useAuth();
  const isSuperAdmin = Boolean(tokens?.isSuperAdmin);

  const [activeTab, setActiveTab] = useState<"profile" | "logs">(initialTab);
  const [logFilter, setLogFilter] = useState<"all" | "access" | "error" | "crucial">("all");

  // Profile form state
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [department, setDepartment] = useState("");
  const [universityId, setUniversityId] = useState("");
  const [streamId, setStreamId] = useState("");
  const [language, setLanguage] = useState("en");
  const [isPremium, setIsPremium] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [premiumMonths, setPremiumMonths] = useState(1);
  const [extendPremium, setExtendPremium] = useState(false);
  const [initialPremium, setInitialPremium] = useState(false);
  const [universities, setUniversities] = useState<Uni[]>([]);
  const [streams, setStreams] = useState<StreamItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [error, setError] = useState("");

  // Logs state
  const [logsData, setLogsData] = useState<UserLogsData | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState("");

  useEffect(() => {
    if (!open || !user) return;
    setActiveTab(initialTab);
    setFullName(user.name);
    setPhone(user.phoneRaw || user.phone);
    setDepartment(user.department || "Freshman");
    setUniversityId(user.universityId || "");
    setStreamId(user.streamId || "");
    setLanguage(user.preferredLanguage || "en");
    const premium = user.isPremium ?? user.plan === "Premium";
    setIsPremium(premium);
    setInitialPremium(premium);
    setIsActive(user.isActive ?? user.status === "Active");
    setPremiumMonths(1);
    setExtendPremium(false);
    setError("");
    setSavedSuccess(false);

    api.admin.catalogUniversities(token)
      .then((d) => setUniversities(d.items.map((u) => ({ id: u.id, name: u.name, short_name: u.short_name }))))
      .catch(() => setUniversities([]));
    api.admin.catalogStreams(token)
      .then((d) => setStreams(d.items.map((s) => ({ id: s.id, name: s.name, code: s.code }))))
      .catch(() => setStreams([]));

    // Load logs for this student
    loadStudentLogs();
  }, [open, user, token, initialTab]);

  function loadStudentLogs() {
    if (!user) return;
    setLogsLoading(true);
    setLogsError("");
    api.admin.userLogs(token, user.id)
      .then((data) => {
        setLogsData(data);
      })
      .catch((err) => {
        setLogsError(err instanceof ApiError ? err.message : "Failed to load student activity logs");
      })
      .finally(() => {
        setLogsLoading(false);
      });
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    setError("");
    try {
      const body: Parameters<typeof api.admin.updateUser>[2] = {
        full_name: fullName.trim(),
        phone: phone.trim(),
        department: department.trim() || null,
        university_id: universityId || null,
        stream_id: streamId || null,
        preferred_language: language as "en" | "am" | "oro",
        is_active: isActive,
      };
      if (isPremium !== initialPremium) {
        body.is_premium = isPremium;
        if (isPremium) body.premium_months = Number(premiumMonths) || 1;
      } else if (isPremium && extendPremium) {
        body.premium_months = Number(premiumMonths) || 1;
      }
      await api.admin.updateUser(token, user.id, body);
      setSavedSuccess(true);
      setTimeout(() => {
        onSaved();
        onClose();
      }, 700);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save student");
    } finally {
      setSaving(false);
    }
  }

  const filteredLogs = logsData?.items.filter((item) => {
    if (logFilter === "access") return item.category === "access" || item.action?.includes("access");
    if (logFilter === "error") {
      return (
        item.category === "error" ||
        item.severity === "error" ||
        item.severity === "warning" ||
        (item.statusCode && item.statusCode >= 400)
      );
    }
    if (logFilter === "crucial") {
      return (
        item.category === "crucial" ||
        item.severity === "crucial" ||
        item.category === "admins" ||
        item.category === "payments"
      );
    }
    return true;
  }) || [];

  if (!open) return null;

  const innerContent = (
    <div className="space-y-4">
      {/* Modal / Section Tab Switcher */}
      <div className="flex items-center gap-2 border-b border-border mb-4 pb-1">
        <button
          type="button"
          onClick={() => setActiveTab("profile")}
          className={`px-3 py-1.5 text-xs font-semibold rounded-t-md transition border-b-2 -mb-[5px] ${
            activeTab === "profile"
              ? "border-primary text-foreground bg-primary/5"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          Profile & Plan
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("logs")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-t-md transition border-b-2 -mb-[5px] ${
            activeTab === "logs"
              ? "border-primary text-foreground bg-primary/5"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <Activity size={13} />
          Activity & Error Logs
          {logsData && logsData.summary.errorCount > 0 && (
            <span className="ml-1 px-1.5 py-0.2 bg-destructive/15 text-destructive font-mono text-xs rounded-full font-bold">
              {logsData.summary.errorCount}
            </span>
          )}
        </button>
      </div>

      {activeTab === "profile" ? (
        <form onSubmit={handleSave} className="space-y-4">
          {error && <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">{error}</p>}

          <div className="grid grid-cols-2 gap-4">
            <label className="block space-y-1.5 col-span-2 sm:col-span-1">
              <span className="text-xs font-semibold uppercase text-muted-foreground">Full name</span>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="w-full px-3 py-2 rounded-md border border-border text-sm outline-none focus:border-primary bg-background"
              />
            </label>
            <label className="block space-y-1.5 col-span-2 sm:col-span-1">
              <span className="text-xs font-semibold uppercase text-muted-foreground">Phone</span>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                className="w-full px-3 py-2 rounded-md border border-border text-sm outline-none focus:border-primary bg-background font-mono"
              />
            </label>
            <label className="block space-y-1.5 col-span-2 sm:col-span-1">
              <span className="text-xs font-semibold uppercase text-muted-foreground">University</span>
              <select
                value={universityId}
                onChange={(e) => setUniversityId(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-border text-sm outline-none focus:border-primary bg-background"
              >
                <option value="">— None —</option>
                {universities.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} ({u.short_name})</option>
                ))}
              </select>
            </label>
            <label className="block space-y-1.5 col-span-2 sm:col-span-1">
              <span className="text-xs font-semibold uppercase text-muted-foreground">Stream</span>
              <select
                value={streamId}
                onChange={(e) => setStreamId(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-border text-sm outline-none focus:border-primary bg-background"
              >
                <option value="">— None / Common —</option>
                {streams.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.code || s.name})</option>
                ))}
              </select>
            </label>
            <label className="block space-y-1.5 col-span-2 sm:col-span-1">
              <span className="text-xs font-semibold uppercase text-muted-foreground">Department</span>
              <input
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-border text-sm outline-none focus:border-primary bg-background"
              />
            </label>
            <label className="block space-y-1.5 col-span-2 sm:col-span-1">
              <span className="text-xs font-semibold uppercase text-muted-foreground">Language</span>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-border text-sm bg-background"
              >
                <option value="en">English</option>
                <option value="am">Amharic</option>
                <option value="oro">Oromo</option>
              </select>
            </label>
            {isPremium && (
              <label className="block space-y-1.5 col-span-2 sm:col-span-1">
                <span className="text-xs font-semibold uppercase text-muted-foreground">
                  {initialPremium ? "Extend premium (months)" : "Premium duration (months)"}
                </span>
                {initialPremium && (
                  <label className="flex items-center gap-2 text-sm mb-1.5">
                    <input type="checkbox" checked={extendPremium} onChange={(e) => setExtendPremium(e.target.checked)} />
                    Extend from expiry (or now)
                  </label>
                )}
                {(!initialPremium || extendPremium) && (
                  <div className="space-y-1">
                    <input
                      type="number"
                      min={1}
                      max={24}
                      value={premiumMonths}
                      onChange={(e) => setPremiumMonths(Math.max(1, Math.min(24, parseInt(e.target.value) || 1)))}
                      className="w-full px-3 py-2 rounded-md border border-border text-sm bg-background"
                      placeholder="1-24 months"
                    />
                    <span className="text-xs text-muted-foreground block">
                      Calendar months (1 to 24)
                    </span>
                  </div>
                )}
              </label>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              type="button"
              onClick={() => setIsActive((v) => !v)}
              className={`px-4 py-2.5 rounded-md border text-sm font-semibold text-left transition ${
                isActive ? "border-success/30 bg-success/10 text-success" : "border-destructive/30 bg-destructive/10 text-destructive"
              }`}
            >
              Account: {isActive ? "Active" : "Suspended"}
            </button>
            <button
              type="button"
              onClick={() => setIsPremium((v) => !v)}
              className={`px-4 py-2.5 rounded-md border text-sm font-semibold text-left transition ${
                isPremium ? "border-warning/30 bg-warning/10 text-warning" : "border-border bg-muted text-muted-foreground"
              }`}
            >
              Plan: {isPremium ? "Premium" : "Free"}
            </button>
          </div>

          <div className="flex gap-3 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 rounded-md border border-border font-medium text-sm hover:bg-muted transition"
            >
              Cancel
            </button>
            <Button
              type="submit"
              loading={saving}
              loadingText="Saving…"
              success={savedSuccess}
              successText="Student Saved!"
              variant="primary"
              className="flex-1 py-2"
            >
              Save changes
            </Button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          {/* Logs KPI Cards */}
          <div className="grid grid-cols-4 gap-2.5">
            <div className="p-3 bg-muted/40 border border-border rounded-md text-left">
              <span className="text-xs font-semibold text-muted-foreground uppercase block">All Events</span>
              <span className="text-xl font-bold font-display text-foreground">
                {logsData?.summary.totalEvents ?? 0}
              </span>
            </div>
            <div className="p-3 bg-info/10 border border-info/30 rounded-md text-left">
              <span className="text-xs font-semibold text-info uppercase block">Accessed</span>
              <span className="text-xl font-bold font-display text-info">
                {logsData?.summary.accessCount ?? 0}
              </span>
            </div>
            <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-left">
              <span className="text-xs font-semibold text-destructive uppercase block">Errors Faced</span>
              <span className="text-xl font-bold font-display text-destructive">
                {logsData?.summary.errorCount ?? 0}
              </span>
            </div>
            <div className="p-3 bg-success/10 border border-success/30 rounded-md text-left">
              <span className="text-xs font-semibold text-success uppercase block">Crucial / Pay</span>
              <span className="text-xl font-bold font-display text-success">
                {logsData?.summary.crucialCount ?? 0}
              </span>
            </div>
          </div>

          {/* Error Banner Callout if errors found */}
          {logsData && logsData.summary.errorCount > 0 && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-2.5">
              <ShieldAlert size={16} className="text-destructive shrink-0 mt-0.5" />
              <div className="text-xs text-destructive">
                <span className="font-semibold block">Issues detected for this student</span>
                <span>
                  This account faced {logsData.summary.errorCount} error(s) (e.g. premium content blocks, rate limits, or payment errors). Review details below.
                </span>
              </div>
            </div>
          )}

          {/* Sub-tabs / Filters */}
          <div className="flex items-center justify-between gap-2 pt-1 border-b border-border pb-2">
            <div className="flex items-center gap-1.5">
              {(
                [
                  { id: "all", label: "All Events" },
                  { id: "access", label: "Content Access" },
                  { id: "error", label: "Errors Faced" },
                  { id: "crucial", label: "Crucial & Security" },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setLogFilter(tab.id)}
                  className={`px-2.5 py-1 text-xs font-semibold rounded transition ${
                    logFilter === tab.id
                      ? "bg-foreground text-background"
                      : "bg-muted/60 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={loadStudentLogs}
              disabled={logsLoading}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              {logsLoading ? <Loader2 size={12} className="animate-spin" /> : "Refresh"}
            </button>
          </div>

          {/* Logs List */}
          {logsLoading ? (
            <div className="py-12 flex flex-col items-center justify-center text-muted-foreground">
              <Loader2 size={24} className="animate-spin mb-2" />
              <span className="text-xs">Loading student audit & error logs…</span>
            </div>
          ) : logsError ? (
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-xs text-destructive">
              {logsError}
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-xs border border-dashed border-border rounded-md">
              No log entries match the selected filter for this student.
            </div>
          ) : (
            <div className="divide-y divide-border border border-border rounded-md overflow-hidden bg-card">
              {filteredLogs.map((item) => {
                const isError = item.category === "error" || item.severity === "error" || (item.statusCode && item.statusCode >= 400);
                const isWarning = item.severity === "warning" || item.statusCode === 403;
                const isAccess = item.category === "access" || item.action?.includes("access");
                const isCrucial = item.category === "crucial" || item.severity === "crucial" || item.category === "admins";

                return (
                  <div
                    key={item.id}
                    className={`p-3 text-left transition flex items-start justify-between gap-3 ${
                      isError
                        ? "bg-destructive/5 hover:bg-destructive/10"
                        : isWarning
                        ? "bg-warning/10 hover:bg-warning/10"
                        : "hover:bg-muted/40"
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5 mb-1">
                        {isError ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-bold uppercase tracking-wider bg-destructive text-destructive-foreground">
                            <ShieldAlert size={10} /> Error
                          </span>
                        ) : isWarning ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-bold uppercase tracking-wider bg-warning/10 text-warning">
                            <Lock size={10} /> Blocked / 403
                          </span>
                        ) : isAccess ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-bold uppercase tracking-wider bg-info/10 text-info">
                            <BookOpen size={10} /> Access
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-bold uppercase tracking-wider bg-success/10 text-success">
                            <Shield size={10} /> Crucial
                          </span>
                        )}

                        {item.statusCode && (
                          <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-muted border border-border text-foreground font-semibold">
                            HTTP {item.statusCode}
                          </span>
                        )}

                        <span className="text-xs text-muted-foreground ml-auto flex items-center gap-1">
                          <Clock size={10} />
                          {item.time}
                        </span>
                      </div>

                      <p className="text-xs font-semibold text-foreground leading-snug">
                        {item.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 break-words">
                        {item.detail}
                      </p>

                      {item.endpoint && (
                        <div className="mt-1 flex items-center gap-2">
                          <span className="font-mono text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded truncate max-w-sm">
                            {item.endpoint}
                          </span>
                          {item.errorCode && (
                            <span className="text-xs font-mono text-destructive">
                              code: {item.errorCode}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="pt-2 text-right">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md border border-border font-medium text-xs hover:bg-muted transition"
            >
              {inline ? "Back to students" : "Close"}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (inline) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-sm p-4 sm:p-6 space-y-6">
        <div className="flex items-center justify-between gap-4 pb-4 border-b border-border flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={onClose}
              className="btn bg-secondary text-foreground px-3 py-1.5 rounded-xs text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-secondary/80 shrink-0"
            >
              <ArrowLeft size={13} />
              <span>Back to list</span>
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base sm:text-lg font-bold text-foreground truncate">
                  {user ? user.name : "Student details"}
                </h2>
                {user && <StatusBadge status={user.plan} />}
                {user && <StatusBadge status={user.status} />}
              </div>
              {user && (
                <p className="text-xs text-muted-foreground mt-0.5 font-mono truncate">
                  {user.phoneRaw || user.phone} · {user.university || "No university"} · Joined {user.joined}
                </p>
              )}
            </div>
          </div>
        </div>

        {innerContent}
      </div>
    );
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={user ? user.name : "Student details"}
      subtitle={user ? `Student profile · ${user.phoneRaw || user.phone} · joined ${user.joined}` : undefined}
      wide
    >
      {innerContent}
    </ModalShell>
  );
}
